import { useEffect, useState, useRef, useCallback } from 'react'
import { X, ChevronLeft, ChevronRight, Heart, MessageCircle } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useGallery } from '../../context/GalleryContext'
import type { GalleryImage, LightboxState } from '../../types'
import GalleryPurchaseModal from '../GalleryPurchaseModal'

// Swiper imports
import { Swiper, SwiperSlide } from 'swiper/react'
import { Zoom, Navigation, Keyboard, A11y, Virtual } from 'swiper/modules'
import type { Swiper as SwiperClass } from 'swiper'

// Swiper styles - REQUIRED
import 'swiper/css'
import 'swiper/css/zoom'
import 'swiper/css/navigation'

interface LightboxProps {
  images: GalleryImage[]
  state: LightboxState
  onClose: () => void
  onIndexChange?: (index: number) => void
}

export function Lightbox({
  images,
  state,
  onClose,
  onIndexChange
}: LightboxProps) {
  const swiperRef = useRef<SwiperClass | null>(null)
  const [isZoomed, setIsZoomed] = useState(false)
  const [showHints, setShowHints] = useState(true)
  // Track if swiper has reported a slide change yet
  const [swiperIndex, setSwiperIndex] = useState<number | null>(null)

  // Likes, navigation, and purchases
  const { likedImageIds, toggleLike, markImageAsPurchased, refreshImageUrl } = useGallery()
  const navigate = useNavigate()
  const [purchaseModalImage, setPurchaseModalImage] = useState<GalleryImage | null>(null)

  const { isOpen, isClosing } = state

  // The active index: use swiper's reported index if available, otherwise use state.currentIndex
  // This ensures on initial open we use the correct index from state immediately
  const displayIndex = swiperIndex !== null ? swiperIndex : state.currentIndex

  // Reset swiperIndex when lightbox closes so next open uses state.currentIndex
  useEffect(() => {
    if (!isOpen) {
      setSwiperIndex(null)
    }
  }, [isOpen])

  // Keyboard close on Escape (Swiper handles arrow keys)
  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === 'ArrowUp') {
        e.preventDefault()
        onClose()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])

  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) {
      const originalOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'
      return () => {
        document.body.style.overflow = originalOverflow
      }
    }
  }, [isOpen])

  // Hide hints after 3 seconds
  useEffect(() => {
    if (isOpen && !isClosing) {
      setShowHints(true)
      const timer = setTimeout(() => setShowHints(false), 3000)
      return () => clearTimeout(timer)
    }
  }, [isOpen, isClosing])

  // Handle slide change - update swiper index for UI
  const handleSlideChange = useCallback((swiper: SwiperClass) => {
    const newIndex = swiper.activeIndex
    setSwiperIndex(newIndex)
    onIndexChange?.(newIndex)
  }, [onIndexChange])

  // Handle zoom change
  const handleZoomChange = useCallback((_swiper: SwiperClass, scale: number) => {
    setIsZoomed(scale > 1)
  }, [])

  // Track which image IDs we've already requested full URLs for (prevent duplicate fetches)
  const preloadedIdsRef = useRef<Set<string>>(new Set())

  // Preload adjacent images with full-res URLs
  useEffect(() => {
    if (!isOpen) {
      preloadedIdsRef.current.clear()
      return
    }

    // Only preload 3 before and 3 after current
    const preloadOffsets = [-3, -2, -1, 1, 2, 3]
    preloadOffsets.forEach(offset => {
      const idx = displayIndex + offset
      if (idx >= 0 && idx < images.length) {
        const img = images[idx]
        if (img?.hasAccess) {
          const url = img.urls.full || img.urls.medium
          if (url) {
            // URL already available - preload the image in browser cache
            const preload = new Image()
            preload.src = url
          } else if (!preloadedIdsRef.current.has(img.id)) {
            // URL is null (thumbnailOnly mode) - fetch full-res URL
            // refreshImageUrl updates images in context, so next render will have the URL
            preloadedIdsRef.current.add(img.id)
            refreshImageUrl(img.id, 'full').catch(() => {})
          }
        }
      }
    })
  }, [isOpen, displayIndex, images, refreshImageUrl])

  if (!isOpen) return null

  // Use displayIndex for dynamic UI (background color, counter, etc.)
  const currentImage = images[displayIndex]
  const bgColor = currentImage?.dominant_color || '#0a0a0a'

  return (
    <div
      className={`fixed inset-0 z-[100] ${isClosing ? 'animate-lightbox-close' : 'animate-lightbox-open'}`}
    >
      {/* FULL SCREEN colored background - transitions smoothly between images */}
      <div
        className="absolute inset-0 transition-colors duration-500 ease-out"
        style={{
          backgroundColor: bgColor,
        }}
      />

      {/* Subtle gradient overlay for depth */}
      <div
        className="absolute inset-0 transition-opacity duration-500"
        style={{
          background: `
            radial-gradient(ellipse at center, transparent 0%, rgba(0,0,0,0.3) 100%),
            linear-gradient(180deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.3) 100%)
          `
        }}
      />

      {/* Swiper Container - Using Virtual for performance */}
      <Swiper
        modules={[Zoom, Navigation, Keyboard, A11y, Virtual]}
        onSwiper={(swiper) => { swiperRef.current = swiper }}
        onSlideChange={handleSlideChange}
        onZoomChange={handleZoomChange}

        // Core settings
        initialSlide={state.currentIndex}
        slidesPerView={1}
        spaceBetween={20}
        centeredSlides={true}

        // Speed settings - important for smooth feel
        speed={300}

        // Virtual slides - CRITICAL FOR PERFORMANCE
        virtual={{
          enabled: true,
          addSlidesBefore: 3,
          addSlidesAfter: 3,
        }}

        // Zoom settings
        zoom={{
          maxRatio: 4,
          minRatio: 1,
          toggle: true,
        }}

        // Touch settings - optimized for smooth mobile swipe
        touchRatio={1}
        threshold={10}
        resistance={true}
        resistanceRatio={0.85}
        followFinger={true}
        longSwipes={true}
        longSwipesRatio={0.4}
        shortSwipes={true}
        touchStartPreventDefault={false}
        passiveListeners={true}

        // Navigation
        navigation={{
          prevEl: '.lightbox-prev',
          nextEl: '.lightbox-next',
        }}

        // Keyboard (arrow keys)
        keyboard={{
          enabled: true,
          onlyInViewport: true,
        }}

        // Accessibility
        a11y={{
          enabled: true,
          prevSlideMessage: 'Previous image',
          nextSlideMessage: 'Next image',
        }}

        className="h-full w-full"
      >
        {images.map((image, index) => (
          <SwiperSlide
            key={image.id}
            virtualIndex={index}
            className="!flex items-center justify-center"
          >
            <div className="swiper-zoom-container w-full h-full flex items-center justify-center">
              <LightboxImage
                image={image}
                isActive={index === displayIndex}
                shouldLoad={Math.abs(index - displayIndex) <= 2}
                onPurchase={() => setPurchaseModalImage(image)}
                refreshImageUrl={refreshImageUrl}
              />
            </div>
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Controls - hide when zoomed */}
      <div className={`transition-opacity duration-200 ${isZoomed ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-50 w-10 h-10 sm:w-11 sm:h-11 bg-black/50 hover:bg-black/70
                     backdrop-blur-md rounded-full flex items-center justify-center
                     transition-all duration-200 active:scale-95"
          aria-label="Close"
        >
          <X size={22} className="text-white" />
        </button>

        {/* Heart button - bottom right (same position as grid view) */}
        {currentImage && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
              toggleLike(currentImage.id)
            }}
            className="absolute bottom-20 right-4 z-50 w-10 h-10 sm:w-11 sm:h-11 bg-black/50 hover:bg-black/70
                       backdrop-blur-md rounded-full flex items-center justify-center
                       transition-all duration-200 active:scale-95"
            aria-label={likedImageIds.has(currentImage.id) ? "Unlike" : "Like"}
            style={{ touchAction: 'manipulation' }}
          >
            <Heart
              size={20}
              className={likedImageIds.has(currentImage.id) ? 'text-red-500 fill-red-500' : 'text-white'}
            />
          </button>
        )}

        {/* Message button - bottom left (same position as grid view) */}
        {currentImage && (
          <button
            onClick={() => navigate(`/messages?replyToGalleryImage=${currentImage.id}`)}
            className="absolute bottom-20 left-4 z-50 w-10 h-10 sm:w-11 sm:h-11 bg-black/50 hover:bg-black/70
                       backdrop-blur-md rounded-full flex items-center justify-center
                       transition-all duration-200 active:scale-95"
            aria-label="Send message about this image"
          >
            <MessageCircle size={20} className="text-white" />
          </button>
        )}

        {/* Navigation arrows - visible on all devices */}
        {images.length > 1 && (
          <>
            <button
              className="lightbox-prev absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-50
                         w-10 h-10 sm:w-12 sm:h-12
                         bg-black/50 hover:bg-black/70 backdrop-blur-md rounded-full
                         flex items-center justify-center transition-all duration-200
                         active:scale-95
                         disabled:opacity-30 disabled:cursor-not-allowed"
              aria-label="Previous"
            >
              <ChevronLeft size={24} className="text-white" />
            </button>
            <button
              className="lightbox-next absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-50
                         w-10 h-10 sm:w-12 sm:h-12
                         bg-black/50 hover:bg-black/70 backdrop-blur-md rounded-full
                         flex items-center justify-center transition-all duration-200
                         active:scale-95
                         disabled:opacity-30 disabled:cursor-not-allowed"
              aria-label="Next"
            >
              <ChevronRight size={24} className="text-white" />
            </button>
          </>
        )}

        {/* Bottom info bar */}
        <div className="absolute bottom-0 left-0 right-0 z-50 pointer-events-none">
          <div className="flex flex-col items-center pb-6 gap-2">
            {/* Progress bar */}
            {images.length > 1 && (
              <div className="w-24 sm:w-32 h-1 bg-white/20 rounded-full overflow-hidden">
                <div
                  className="h-full bg-white rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${((displayIndex + 1) / images.length) * 100}%` }}
                />
              </div>
            )}

            {/* Counter */}
            <div className="bg-black/50 backdrop-blur-md text-white text-xs sm:text-sm font-medium rounded-full px-3 py-1">
              {displayIndex + 1} / {images.length}
            </div>

            {/* Swipe hints */}
            {showHints && (
              <div className="text-white/50 text-xs animate-lightbox-hint">
                <span className="hidden sm:inline">← → navigate • double-click to zoom • Esc to close</span>
                <span className="sm:hidden">Swipe or tap arrows • double-tap to zoom</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Zoom indicator */}
      {isZoomed && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-50 bg-black/60 backdrop-blur-md text-white text-xs rounded-full px-3 py-1.5">
          Double-tap to zoom out
        </div>
      )}

      {/* Purchase modal */}
      {purchaseModalImage && (
        <GalleryPurchaseModal
          image={purchaseModalImage}
          onClose={() => setPurchaseModalImage(null)}
          onSuccess={() => {
            markImageAsPurchased(purchaseModalImage.id)
            setPurchaseModalImage(null)
          }}
        />
      )}
    </div>
  )
}

// Individual image component with loading state
interface LightboxImageProps {
  image: GalleryImage
  isActive: boolean
  shouldLoad: boolean
  onPurchase?: () => void
  refreshImageUrl: (imageId: string, resolution: 'medium' | 'full' | 'original') => Promise<string>
}

function LightboxImage({ image, shouldLoad, onPurchase, refreshImageUrl }: LightboxImageProps) {
  const navigate = useNavigate()
  // Two-layer approach: thumbnail underneath, full on top (no flash on swap)
  const [thumbnailLoaded, setThumbnailLoaded] = useState(false)
  const [fullLoaded, setFullLoaded] = useState(false)
  // Retry key: changing this forces img tags to remount and retry loading
  const [retryKey, setRetryKey] = useState(0)
  // Fetched full-res URL (when initial urls.full is null from thumbnailOnly mode)
  const [fetchedFullUrl, setFetchedFullUrl] = useState<string | null>(null)

  // Calculate URLs - prefer fetched full URL over initial (which may be null)
  const thumbnailUrl = image.urls.thumbnail
  const fullUrl = image.hasAccess
    ? (fetchedFullUrl || image.urls.full || image.urls.medium || image.urls.thumbnail)
    : image.urls.thumbnail

  // Track whether we've already retried a refresh for this image
  const hasRetriedFull = useRef(false)
  const isFetchingFullRef = useRef(false)

  // Reset loaded state when image changes
  useEffect(() => {
    setThumbnailLoaded(false)
    setFullLoaded(false)
    setRetryKey(0)
    hasRetriedFull.current = false
    setFetchedFullUrl(null)
    isFetchingFullRef.current = false
  }, [image.id])

  // Fetch full-res URL when it's null (thumbnailOnly mode) and we should load
  useEffect(() => {
    if (!shouldLoad || !image.hasAccess) return
    if (image.urls.full || fetchedFullUrl) return
    if (isFetchingFullRef.current) return

    isFetchingFullRef.current = true
    refreshImageUrl(image.id, 'full')
      .then(url => {
        setFetchedFullUrl(url)
      })
      .catch(() => {
        // Failed to fetch full URL — thumbnail stays visible
      })
      .finally(() => {
        isFetchingFullRef.current = false
      })
  }, [shouldLoad, image.hasAccess, image.id, image.urls.full, fetchedFullUrl, refreshImageUrl])

  // When full-res img fails (expired signed URL), refresh and retry once
  const handleFullError = useCallback(async () => {
    if (hasRetriedFull.current) return
    hasRetriedFull.current = true
    try {
      await refreshImageUrl(image.id, 'full')
      setRetryKey(k => k + 1)
    } catch {
      // Refresh failed — thumbnail stays visible
    }
  }, [image.id, refreshImageUrl])

  // Timeout: if nothing loads after 5 seconds, force retry by remounting img tags
  useEffect(() => {
    if (!shouldLoad) return
    if (thumbnailLoaded || fullLoaded) return // Already loaded, no need for timeout

    const timer = setTimeout(() => {
      if (!thumbnailLoaded && !fullLoaded) {
        setRetryKey(k => k + 1)
      }
    }, 5000)

    return () => clearTimeout(timer)
  }, [shouldLoad, thumbnailLoaded, fullLoaded, retryKey])

  // Simple logic: if shouldLoad is true, we load
  // No complex tracking - just render the img tags and let browser handle it
  const showThumbnail = shouldLoad && thumbnailUrl && !fullLoaded
  const showFull = shouldLoad && fullUrl

  // Access gate - different UI for premium vs subscriber content
  if (!image.hasAccess) {
    const isPremium = image.access_level === 'premium'
    const price = image.price ?? 0
    const isFree = price === 0

    return (
      <div className="flex items-center justify-center p-4 sm:p-8 w-full h-full">
        <div className="text-center text-white bg-white/5 backdrop-blur-md rounded-2xl p-6 sm:p-8 max-w-sm">
          {/* Blurry preview background */}
          {image.urls.placeholder && (
            <div
              className="absolute inset-0 rounded-2xl overflow-hidden -z-10"
              style={{
                backgroundImage: `url(${image.urls.placeholder})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                filter: 'blur(40px)',
                opacity: 0.2
              }}
            />
          )}

          <div className="w-14 h-14 sm:w-16 sm:h-16 mx-auto mb-4 rounded-full bg-white/10 flex items-center justify-center">
            <svg className="w-7 h-7 sm:w-8 sm:h-8 text-white/60" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>

          {isPremium ? (
            <>
              {/* Premium content - show price and purchase button */}
              <p className={`text-2xl font-bold mb-2 ${isFree ? 'text-green-400' : 'text-white'}`}>
                {isFree ? 'FREE' : `$${price.toFixed(2)}`}
              </p>
              <p className="text-white/60 text-sm mb-4">
                {isFree ? 'Unlock this free image' : 'Purchase to unlock this image'}
              </p>
              <button
                onClick={onPurchase}
                className={`px-6 py-2 rounded-full font-medium transition-colors ${
                  isFree
                    ? 'bg-green-500 text-white hover:bg-green-600'
                    : 'bg-purple-500 text-white hover:bg-purple-600'
                }`}
              >
                {isFree ? 'Unlock Free' : 'Unlock'}
              </button>
            </>
          ) : (
            <>
              {/* Subscriber content */}
              <p className="text-base sm:text-lg font-medium mb-2">Subscribe to View</p>
              <p className="text-white/60 text-sm mb-4">This content is for subscribers only</p>
              <button
                onClick={() => navigate('/subscription')}
                className="px-6 py-2 bg-white text-black rounded-full font-medium hover:bg-gray-100 transition-colors"
              >
                Subscribe
              </button>
            </>
          )}
        </div>
      </div>
    )
  }

  // Show spinner only if nothing has loaded yet
  const hasAnythingLoaded = thumbnailLoaded || fullLoaded
  const showSpinner = shouldLoad && !hasAnythingLoaded

  return (
    <div className="w-full h-full flex items-center justify-center">
      {/* Loading spinner - only show if nothing visible yet */}
      {showSpinner && (
        <div className="absolute inset-0 flex items-center justify-center z-10">
          <div className="w-8 h-8 sm:w-10 sm:h-10 border-2 border-white/20 border-t-white rounded-full animate-spin" />
        </div>
      )}

      {/* Two-layer image container - thumbnail underneath, full on top */}
      {/* This prevents any flash when transitioning from thumbnail to full */}
      <div className="relative max-w-[90vw] max-h-[85vh] flex items-center justify-center">
        {/* Layer 1: Thumbnail (shows immediately, stays until full is ready) */}
        {showThumbnail && (
          <img
            key={`thumb-${retryKey}`}
            src={thumbnailUrl}
            alt={image.title || ''}
            loading="eager"
            decoding="async"
            onLoad={() => setThumbnailLoaded(true)}
            className={`max-w-[90vw] max-h-[85vh] object-contain select-none rounded-lg transition-opacity duration-150 ${
              thumbnailLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            style={{
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.1)',
            }}
            draggable={false}
          />
        )}

        {/* Layer 2: Full resolution (loads on top, replaces thumbnail when ready) */}
        {showFull && (
          <img
            key={`full-${retryKey}`}
            src={fullUrl}
            alt={image.title || ''}
            loading="eager"
            decoding="async"
            onLoad={() => setFullLoaded(true)}
            onError={handleFullError}
            className={`max-w-[90vw] max-h-[85vh] object-contain select-none rounded-lg transition-opacity duration-150 ${
              fullLoaded ? 'opacity-100' : 'opacity-0'
            } ${thumbnailLoaded && !fullLoaded ? 'absolute inset-0 m-auto' : ''}`}
            style={{
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.1)',
            }}
            draggable={false}
          />
        )}
      </div>
    </div>
  )
}
